// src/layouts/trafficmonitoring/monitoring/RouteView.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import MDBox from "components/MDBox";

import TrafficList from "./TrafficList";
import TrafficMonitoringMap from "./trafficMonitoringMap/TrafficMonitoringMap";

import useRouteData from "../data/useRouteData";
import useTrafficMonitoringGeom from "../data/useTrafficMonitoringGeom";
import { fetchActiveEvents } from "../data/fetchActiveEvents";
import useNetworkData from "../data/useNetworkData";
import TrafficScrollBar from "customComponents/customTabs.js/CustomTabs";

const MemoTrafficList = React.memo(TrafficList);
const MemoTrafficMap = React.memo(TrafficMonitoringMap);

export default function RouteView() {
  // 1️⃣ Poll routes & network geometry
  const { data: routes, loading: loadingRoutes } = useRouteData();
  const { data: netData, loading: loadingNetwork } = useTrafficMonitoringGeom();

  // 2️⃣ Local state & refs for “first load”
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [networkSource, setNetworkSource] = useState(null);
  const [onlyActive, setOnlyActive] = useState([]);
  const [onlyJam, setOnlyJam] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const hasLoadedNetwork = useRef(false);
  const hasLoadedEvents = useRef(false);

  // 3️⃣ Default‐select the very first route
  useEffect(() => {
    if (!Array.isArray(routes) || routes.length === 0) return;

    // If nothing selected yet, default to first:
    if (!selectedRoute) {
      setSelectedRoute(routes[0]);
      return;
    }

    // Try to find a matching route in the new array by ID:
    const fresh = routes.find((r) => r.route_id === selectedRoute.route_id);
    if (fresh) {
      // Re-assign to the new object reference
      setSelectedRoute(fresh);
    } else {
      // Fallback if it really vanished
      setSelectedRoute(routes[0]);
    }
  }, [routes]);

  // 4️⃣ Build GeoJSON for the selected route
  useEffect(() => {
    if (selectedRoute && netData?.features) {
      // const filtered = netData.features
      //   .filter(f => f.properties.route_id === selectedRoute.route_id)
      //   .map(f => {
      //     const flatCoords = f.geometry.coordinates
      //       .reduce((allPoints, segment) => allPoints.concat(segment), []);
      //     return {
      //       ...f,
      //       geometry: {
      //         type: "LineString",
      //         coordinates: flatCoords,
      //       },
      //     }
      //   })
      // setNetworkSourceEvents({
      //   type: "FeatureCollection",
      //   features: filtered
      // });

      setNetworkSource({
        type: "FeatureCollection",
        features: netData.features.filter(
          (f) => f.properties.route_id === selectedRoute.route_id
        ),
      });
    } else {
      setNetworkSource(null);
      // setNetworkSourceEvents(null)
    }
  }, [netData, selectedRoute]);

  // 5️⃣ Fetch & poll events every minute
  useEffect(() => {
    if (!networkSource?.features?.length) {
      setOnlyActive([]);
      setOnlyJam([]);
      setLoadingEvents(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingEvents(true);
      try {
        const filtered = netData.features
          .filter((f) => f.properties.route_id === selectedRoute.route_id)
          .map((f) => {
            const flatCoords = f.geometry.coordinates.reduce(
              (allPoints, segment) => allPoints.concat(segment),
              []
            );
            return {
              ...f,
              geometry: {
                type: "LineString",
                coordinates: flatCoords,
              },
            };
          });
        const passData = {
          type: "FeatureCollection",
          features: filtered,
        };

        const ev = await fetchActiveEvents(passData, "/events/route");
        // console.log("route ev", ev);
        if (!cancelled) {
          setOnlyActive(ev);
          setOnlyJam(ev.filter((e) => e.category?.id === 10));
        }
      } catch {
        if (!cancelled) {
          setOnlyActive([]);
          setOnlyJam([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingEvents(false);
          hasLoadedEvents.current = true;
        }
      }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [networkSource]);

  // 4️⃣ Memos (always run before returns)
  const routeMetrics = useMemo(() => selectedRoute?.metrics || [], [
    selectedRoute,
  ]);

  // 6️⃣ Memoize the stats arrays
  const dataOfEvents = useMemo(
    () => [
      {
        name: "Number of Events",
        values: [
          { label: "Total", value: onlyActive.length, status: "neutral" },
        ],
        description: `Total <strong>number of active events</strong> on this route.`,
      },
    ],
    [onlyActive.length]
  );

  const dataOfJams = useMemo(
    () => [
      {
        name: "Number of Traffic Jams",
        values: [{ label: "Total", value: onlyJam.length, status: "neutral" }],
        description: `Distinct <strong>traffic jams</strong> on this route.`,
      },
    ],
    [onlyJam.length]
  );

  const allMetrics = useMemo(
    () => [...routeMetrics, ...dataOfEvents, ...dataOfJams],
    [routeMetrics, dataOfEvents, dataOfJams]
  );

  // 7️⃣ First block: wait for routes + network
  if ((loadingRoutes || loadingNetwork) && !hasLoadedNetwork.current) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100%" }}
      >
        <CircularProgress />
      </Grid>
    );
  }
  hasLoadedNetwork.current = true;

  // 8️⃣ Second block: wait for first events load
  if (loadingEvents && !hasLoadedEvents.current) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100%" }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  // 9️⃣ Once both have bootstrapped, render the live‐updating view
  return (
    <Grid container spacing={2}>
      {/* Sidebar stats */}
      <Grid
        item
        xs={12}
        md={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "80vh", // match the map height
          maxHeight: "90vh", // optional cap
          py: 1,
        }}
      >
        {/* <MemoTrafficList data={selectedRoute?.metrics || []} />
        <MemoTrafficList data={dataOfEvents} />
        <MemoTrafficList data={dataOfJams} /> */}

        <MemoTrafficList data={allMetrics} />
      </Grid>

      {/* Map + selector */}
      <Grid item xs={12} md={9}>
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "80vh",
            maxHeight: "90vh",
          }}
        >
          
          <Box>
            <TrafficScrollBar
              data={routes}
              selectedData={selectedRoute}
              setSelectedData={setSelectedRoute}
            />

          </Box>

          <MDBox
            sx={{
              flexGrow: 1,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <MemoTrafficMap
              onlyActive={onlyActive}
              networkSource={networkSource}
              areaRectSource={[]}
            />
          </MDBox>
        </MDBox>
      </Grid>
    </Grid>
  );
}
